import { TFunction } from 'i18next';
import _get from 'lodash/get';
import {
  WidgetTemplatesKindEnum,
  WidgetTemplatesVisibilityEnum,
} from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import getColorOptions from '@lib/components/Chip/utils/colorEnum';
import { getWidgetKindOptions } from 'routes/Home/utils/getWidgetTemplateSharedFields';
import { AttachmentIntendedUse } from '@lib/enums/attachments';
import findAttachmentByIntendedUse, {
  AttachmentWithIntendedUse,
} from 'routes/Home/utils/findAttachmentByIntendedUse';

/**
 * Simplified template interface for default values generation
 */
interface DefaultValuesWidgetTemplate {
  title: string | null;
  description?: string | null;
  kind: WidgetTemplatesKindEnum;
  visibility: WidgetTemplatesVisibilityEnum;
  fields?: {
    backgroundColor?: string | null;
    content?: string | null;
    link?: string | null;
  } | null;
  attachmentsAttached?: AttachmentWithIntendedUse[] | null;
}

/**
 * Converts a WidgetTemplate to form default values for editing
 */
export default function getWidgetTemplateDefaultValues(
  template: DefaultValuesWidgetTemplate,
  t: TFunction<'translation', undefined>,
): Values {
  const values: Values = {};

  const { kind, fields: templateFields, attachmentsAttached } = template;
  const fields = templateFields || {};

  // Kind (for RadioButtonGroup - needs option object with value/label)
  const kindOptions = getWidgetKindOptions(t);
  const kindOption = kindOptions.find((opt) => opt.value === kind);
  values.kind = kindOption || null;

  // Title
  values.title = template.title || '';

  // Description
  values.description = template.description || '';

  // Visibility (private toggle)
  values.isPrivate = template.visibility === WidgetTemplatesVisibilityEnum.Prv;

  // Icon image attachment for widget preview (filter by intendedUse)
  const iconAttachment = findAttachmentByIntendedUse(
    attachmentsAttached,
    AttachmentIntendedUse.WidgetIcon,
  );
  if (iconAttachment) {
    values.iconImage = iconAttachment;
  }

  switch (kind) {
    case WidgetTemplatesKindEnum.ExternalLink: {
      const link = _get(fields, 'link') as string | undefined;
      values.link = link || '';
      break;
    }

    case WidgetTemplatesKindEnum.Announcement: {
      // Background color
      const backgroundColor = _get(fields, 'backgroundColor') as
        string | undefined;
      if (backgroundColor) {
        const colorOptions = getColorOptions({ t });
        const colorOption = colorOptions.find(
          (opt) => opt.value === backgroundColor,
        );
        values.backgroundColor = colorOption || null;
      }

      // Content
      const content = _get(fields, 'content') as string | undefined;
      values.content = content || '';

      // Image attachment for Announcement (filter by intendedUse)
      const imageAttachment = findAttachmentByIntendedUse(
        attachmentsAttached,
        AttachmentIntendedUse.WidgetImage,
      );
      if (imageAttachment) {
        values.image = imageAttachment;
      }
      break;
    }

    default:
      break;
  }

  return values;
}
